/**
 * Uploaded photos are stored once here and referenced by id from the site content.
 *
 * Without this, the draft copy and the live copy each held a full duplicate of every
 * uploaded photo, which doubled storage use and pushed the browser past its limit —
 * silently dropping edits that looked saved in the editor.
 */

const storageKey = 'artistos-images';
export const imageRefPrefix = 'stored:';

type ImageStore = Record<string, string>;

function readStore(): ImageStore {
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) return {};
    const parsed: unknown = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? (parsed as ImageStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: ImageStore): void {
  window.localStorage.setItem(storageKey, JSON.stringify(store));
}

function hash(value: string): string {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return (result >>> 0).toString(36) + value.length.toString(36);
}

/** Replaces a data URL with a short reference, storing the image once. */
export function storeImage(value: string): string {
  if (!value.startsWith('data:')) return value;
  const id = hash(value);
  const store = readStore();
  if (!store[id]) {
    store[id] = value;
    writeStore(store);
  }
  return `${imageRefPrefix}${id}`;
}

/** Turns a stored reference back into the data URL the browser can render. */
export function resolveImage(value: string): string {
  if (!value || !value.startsWith(imageRefPrefix)) return value;
  return readStore()[value.slice(imageRefPrefix.length)] ?? '';
}

/** Drops any stored image no longer referenced by the draft or the live site. */
export function pruneImages(usedRefs: string[]): void {
  try {
    const keep = new Set(
      usedRefs.filter((ref) => ref.startsWith(imageRefPrefix)).map((ref) => ref.slice(imageRefPrefix.length)),
    );
    const store = readStore();
    const next: ImageStore = {};
    Object.keys(store).forEach((id) => {
      if (keep.has(id)) next[id] = store[id];
    });
    writeStore(next);
  } catch {
    // Pruning is housekeeping only.
  }
}

export function imageStoreBytes(): number {
  try {
    return window.localStorage.getItem(storageKey)?.length ?? 0;
  } catch {
    return 0;
  }
}
